import { Link, router } from '@inertiajs/react'
import { Card } from '@/components/ui/Card'
import { Heart, MessageCircle, Clock } from 'lucide-react'
import type { Post } from '@/types'

interface LastContentsProps {
  posts: Post[]
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return "Aujourd'hui"
  if (diffDays === 1) return 'Hier'
  if (diffDays < 7) return `Il y a ${diffDays} jours`
  if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} semaine${Math.floor(diffDays / 7) > 1 ? 's' : ''}`
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })
}

function PostCard({ post }: { post: Post }) {
  const handleCardClick = () => {
    router.visit(`/posts/${post.id}`)
  }

  return (
    <Card
      className="hover:shadow-lg hover:border-tessan-primary/30 transition-all cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          {/* Catégorie */}
          <span className="inline-block px-2 py-1 text-xs font-medium bg-tessan-cream text-tessan-primary rounded-full mb-2">
            {post.category}
          </span>

          {/* Titre */}
          <Link href={`/posts/${post.id}`} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-text-dark hover:text-tessan-primary transition-colors mb-2">
              {post.title}
            </h3>
          </Link>

          {/* Extrait */}
          <p className="text-text-gray text-sm mb-3 line-clamp-2">
            {post.excerpt}
          </p>

          {/* Meta */}
          <div className="flex items-center gap-4 text-sm text-text-gray">
            <span className="font-medium">{post.author}</span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {formatDate(post.created_at)}
            </span>
          </div>
        </div>

        {/* Stats */}
        <div className="flex flex-col items-end gap-2 text-sm text-text-gray">
          <span className="flex items-center gap-1">
            <Heart className="w-4 h-4" />
            {post.likes_count}
          </span>
          <span className="flex items-center gap-1">
            <MessageCircle className="w-4 h-4" />
            {post.comments_count}
          </span>
        </div>
      </div>
    </Card>
  )
}

export function LastContents({ posts }: LastContentsProps) {
  if (posts.length === 0) {
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-text-gray mb-4">Aucune publication pour le moment</p>
          <Link
            href="/community"
            className="inline-block px-4 py-2 bg-tessan-primary text-white rounded-lg hover:bg-tessan-primary-hover transition-colors"
          >
            Créer la première publication
          </Link>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {posts.map((post) => (
        <PostCard key={post.id} post={post} />
      ))}

      <div className="text-center pt-2">
        <Link
          href="/community"
          className="text-tessan-primary hover:underline text-sm font-medium"
        >
          Voir toutes les publications →
        </Link>
      </div>
    </div>
  )
}
